import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import styles from './GuildCreation.module.css';

interface EmblemConfig {
  backgroundColor: string;
  iconType: string;
  iconColor: string;
  borderType: string;
  borderColor: string;
}

const BACKGROUND_COLORS = [
  '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF',
  '#800000', '#008000', '#000080', '#808000', '#800080', '#008080',
  '#C0C0C0', '#808080', '#000000', '#FFFFFF'
];

const ICON_TYPES = [
  'sword', 'shield', 'crown', 'dragon', 'eagle', 'lion', 'wolf', 'bear',
  'hammer', 'axe', 'bow', 'star', 'moon', 'sun', 'flame', 'lightning'
];

const ICON_COLORS = [
  '#FFFFFF', '#000000', '#FFD700', '#C0C0C0', '#CD7F32', '#FF0000',
  '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500'
];

const BORDER_TYPES = [
  'simple', 'ornate', 'spikes', 'chains', 'gems', 'runes', 'thorns', 'waves'
];

const BORDER_COLORS = [
  '#FFD700', '#C0C0C0', '#CD7F32', '#000000', '#FFFFFF', '#FF0000',
  '#00FF00', '#0000FF', '#800000', '#008000', '#000080', '#FFA500'
];

const GuildCreation: React.FC = () => {
  const { user } = useAuth();
  const [guildName, setGuildName] = useState('');
  const [guildTag, setGuildTag] = useState('');
  const [emblem, setEmblem] = useState<EmblemConfig>({
    backgroundColor: '#FF0000',
    iconType: 'sword',
    iconColor: '#FFFFFF',
    borderType: 'simple',
    borderColor: '#FFD700'
  });
  const [hasGuildToken, setHasGuildToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkGuildToken();
  }, []);

  const checkGuildToken = async () => {
    try {
      const response = await fetch('/api/inventory/check-guild-token', {
        credentials: 'include'
      });
      const data = await response.json();
      setHasGuildToken(data.hasToken);
    } catch (error) {
      console.error('Error checking guild token:', error);
    }
  };

  const handleEmblemChange = (field: keyof EmblemConfig, value: string) => {
    setEmblem(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateGuild = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!hasGuildToken) {
      setError('You need a Guild Token to create a guild');
      return;
    }

    if (guildName.length < 3 || guildName.length > 30) {
      setError('Guild name must be between 3 and 30 characters');
      return;
    }

    if (guildTag.length < 2 || guildTag.length > 5) {
      setError('Guild tag must be between 2 and 5 characters');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/guild/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          name: guildName,
          tag: guildTag,
          emblem
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to guild dashboard
        window.location.href = '/guild';
      } else {
        setError(data.message || 'Failed to create guild');
      }
    } catch (error) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderEmblemPreview = () => {
    return (
      <div 
        className={styles.emblemPreview}
        style={{
          backgroundColor: emblem.backgroundColor,
          borderColor: emblem.borderColor
        }}
      >
        <div 
          className={`${styles.emblemIcon} ${styles[`icon-${emblem.iconType}`]} ${styles[`border-${emblem.borderType}`]}`}
          style={{ color: emblem.iconColor }}
        >
          {emblem.iconType.charAt(0).toUpperCase()}
        </div>
      </div>
    );
  };

  if (!hasGuildToken) {
    return (
      <div className={styles.container}>
        <div className={styles.noToken}>
          <h2>Guild Creation</h2>
          <p>You need a Guild Token to create a guild.</p>
          <p>Guild Tokens can be obtained through special events or purchased from the shop.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.creationForm}>
        <h2>Create Your Guild</h2>
        
        <form onSubmit={handleCreateGuild}>
          <div className={styles.formGroup}>
            <label htmlFor="guildName">Guild Name</label>
            <input
              type="text"
              id="guildName"
              value={guildName}
              onChange={(e) => setGuildName(e.target.value)}
              placeholder="Enter guild name (3-30 characters)"
              maxLength={30}
              required
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="guildTag">Guild Tag</label>
            <input
              type="text"
              id="guildTag"
              value={guildTag}
              onChange={(e) => setGuildTag(e.target.value.toUpperCase())}
              placeholder="Enter guild tag (2-5 characters)"
              maxLength={5}
              required
            />
          </div>

          <div className={styles.emblemBuilder}>
            <h3>Design Your Emblem</h3>
            
            <div className={styles.emblemPreviewContainer}>
              {renderEmblemPreview()}
            </div>

            <div className={styles.emblemControls}>
              <div className={styles.controlGroup}>
                <label>Background Color</label>
                <div className={styles.colorGrid}>
                  {BACKGROUND_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`${styles.colorButton} ${emblem.backgroundColor === color ? styles.selected : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => handleEmblemChange('backgroundColor', color)}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Icon</label>
                <div className={styles.iconGrid}>
                  {ICON_TYPES.map(icon => (
                    <button
                      key={icon}
                      type="button"
                      className={`${styles.iconButton} ${emblem.iconType === icon ? styles.selected : ''}`}
                      onClick={() => handleEmblemChange('iconType', icon)}
                    >
                      {icon.charAt(0).toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Icon Color</label>
                <div className={styles.colorGrid}>
                  {ICON_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`${styles.colorButton} ${emblem.iconColor === color ? styles.selected : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => handleEmblemChange('iconColor', color)}
                    />
                  ))}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Border Style</label>
                <div className={styles.borderGrid}>
                  {BORDER_TYPES.map(border => (
                    <button
                      key={border}
                      type="button"
                      className={`${styles.borderButton} ${emblem.borderType === border ? styles.selected : ''}`}
                      onClick={() => handleEmblemChange('borderType', border)}
                    >
                      {border}
                    </button>
                  ))}
                </div>
              </div>

              <div className={styles.controlGroup}>
                <label>Border Color</label>
                <div className={styles.colorGrid}>
                  {BORDER_COLORS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`${styles.colorButton} ${emblem.borderColor === color ? styles.selected : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => handleEmblemChange('borderColor', color)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          {error && <div className={styles.error}>{error}</div>}

          <button 
            type="submit" 
            className={styles.createButton}
            disabled={loading}
          >
            {loading ? 'Creating Guild...' : 'Create Guild'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GuildCreation;
